package co.uk.paxes.pinpoint.model

import co.uk.paxes.pinpoint.DebugLogger

/**
 * Authentic PINPOINT score calculator following official rules
 */
class ScoreCalculator {
    
    fun calculateMoveScore(board: Board, move: Move): ScoreResult {
        val position = move.position
        val pin = move.pin
        val color = pin.getEffectiveColor()
        
        if (color == PinColor.BLACK) {
            return ScoreResult(0, emptyList(), emptyList(), emptyList())
        }
        
        var maxPoints = 0
        val pinsToReverse = mutableListOf<Position>()
        val patternsFormed = mutableListOf<List<Position>>()
        
        // 1. Check for LINES
        val longestLine = findLongestLine(board, position, color)
        
        val linePoints = when (longestLine.size) {
            1 -> if (!isAdjacentToSameColor(board, position, color)) 1 else 1
            2 -> 2
            3 -> 3
            4 -> 4
            5 -> 5
            else -> {
                if (longestLine.size >= 6) {
                    pinsToReverse.addAll(longestLine)
                    10
                } else 0
            }
        }
        
        if (linePoints > maxPoints) {
            maxPoints = linePoints
            patternsFormed.clear()
            if (longestLine.size > 1) {
                patternsFormed.add(longestLine)
            }
        }
        
        // 2. Check for SQUARES (2x2 patterns)
        val squareResult = findSquarePattern(board, position, color)
        if (squareResult.points > maxPoints) {
            maxPoints = squareResult.points
            patternsFormed.clear()
            patternsFormed.add(squareResult.positions)
        } else if (squareResult.points == maxPoints && maxPoints > 0) {
            patternsFormed.add(squareResult.positions)
        }
        
        // 3. Check for CROSSES (+ patterns)
        val crossResult = findAllCrossPatterns(board, position, color)
        if (crossResult.points > maxPoints) {
            maxPoints = crossResult.points
            patternsFormed.clear()
            patternsFormed.add(crossResult.positions)
        } else if (crossResult.points == maxPoints && maxPoints > 0) {
            patternsFormed.add(crossResult.positions)
        }
        
        return ScoreResult(maxPoints, pinsToReverse.distinct(), patternsFormed, getScoringPositions(position, patternsFormed))
    }

    
    private fun findLongestLine(board: Board, position: Position, color: PinColor): List<Position> {
        val directions = listOf(
            Pair(0, 1),  // horizontal
            Pair(1, 0),  // vertical
            Pair(1, 1),  // diagonal \
            Pair(1, -1)  // diagonal /
        )
        
        var longestLine = listOf(position)
        
        for ((dRow, dCol) in directions) {
            val line = findLineInDirection(board, position, color, dRow, dCol)
            if (line.size > longestLine.size) {
                longestLine = line
            }
        }
        
        return longestLine
    }
    
    private fun isAdjacentToSameColor(board: Board, position: Position, color: PinColor): Boolean {
        val (row, col) = position.toLegacy()
        
        val adjacentOffsets = listOf(
            Pair(-1, -1), Pair(-1, 0), Pair(-1, 1),
            Pair(0, -1),               Pair(0, 1),
            Pair(1, -1),  Pair(1, 0),  Pair(1, 1)
        )
        
        for ((dRow, dCol) in adjacentOffsets) {
            val newRow = row + dRow
            val newCol = col + dCol
            if (newRow in 0..9 && newCol in 0..9) {
                val adjacentPos = Position.fromLegacy(newRow, newCol)
                val adjacentColor = board.getPinAt(adjacentPos)?.getEffectiveColor()
                if (adjacentColor == color && adjacentColor != PinColor.BLACK) {
                    return true
                }
            }
        }
        
        return false
    }
    
    private fun findLineInDirection(
        board: Board, 
        startPos: Position, 
        color: PinColor, 
        dRow: Int, 
        dCol: Int
    ): List<Position> {
        val line = mutableListOf<Position>()
        val (startRow, startCol) = startPos.toLegacy()
        
        // Check backward direction
        var currentRow = startRow - dRow
        var currentCol = startCol - dCol
        while (currentRow in 0..9 && currentCol in 0..9) {
            val pos = Position.fromLegacy(currentRow, currentCol)
            val pinColor = board.getPinAt(pos)?.getEffectiveColor()
            if (pinColor == color && pinColor != PinColor.BLACK) {
                line.add(0, pos)
                currentRow -= dRow
                currentCol -= dCol
            } else {
                break
            }
        }
        
        line.add(startPos)
        
        // Check forward direction
        currentRow = startRow + dRow
        currentCol = startCol + dCol
        while (currentRow in 0..9 && currentCol in 0..9) {
            val pos = Position.fromLegacy(currentRow, currentCol)
            val pinColor = board.getPinAt(pos)?.getEffectiveColor()
            if (pinColor == color && pinColor != PinColor.BLACK) {
                line.add(pos)
                currentRow += dRow
                currentCol += dCol
            } else {
                break
            }
        }
        
        return line
    }

    
    private fun findSquarePattern(board: Board, position: Position, color: PinColor): PatternResult {
        val (row, col) = position.toLegacy()
        
        val squareOffsets = listOf(
            listOf(Pair(0, 0), Pair(0, 1), Pair(1, 0), Pair(1, 1)),
            listOf(Pair(0, -1), Pair(0, 0), Pair(1, -1), Pair(1, 0)),
            listOf(Pair(-1, 0), Pair(-1, 1), Pair(0, 0), Pair(0, 1)),
            listOf(Pair(-1, -1), Pair(-1, 0), Pair(0, -1), Pair(0, 0))
        )
        
        for (offsets in squareOffsets) {
            val squarePositions = offsets.mapNotNull { (dRow, dCol) ->
                val newRow = row + dRow
                val newCol = col + dCol
                if (newRow in 0..9 && newCol in 0..9) {
                    Position.fromLegacy(newRow, newCol)
                } else null
            }
            
            if (squarePositions.size == 4) {
                if (color != PinColor.BLACK && squarePositions.all { pos -> 
                    if (pos == position) true
                    else {
                        val pinColor = board.getPinAt(pos)?.getEffectiveColor()
                        pinColor == color && pinColor != PinColor.BLACK
                    }
                }) {
                    val noPinsOnGridLines = isSquareOffGridLines(squarePositions)
                    val points = if (noPinsOnGridLines) 8 else 4
                    return PatternResult(points, squarePositions)
                }
            }
        }
        
        return PatternResult(0, emptyList())
    }
    
    private fun findCrossPattern(board: Board, newPinPosition: Position, centerPosition: Position, color: PinColor): PatternResult {
        val (row, col) = centerPosition.toLegacy()
        
        val upRow = row - 1
        val downRow = row + 1
        val leftCol = col - 1
        val rightCol = col + 1
        
        if (upRow < 0 || downRow > 9 || leftCol < 0 || rightCol > 9) {
            return PatternResult(0, emptyList())
        }
        
        val requiredPositions = listOf(
            centerPosition,
            Position.fromLegacy(upRow, col),
            Position.fromLegacy(downRow, col),
            Position.fromLegacy(row, leftCol),
            Position.fromLegacy(row, rightCol)
        )
        
        val validCrossPositions = requiredPositions.filter { pos ->
            if (pos == newPinPosition) {
                color != PinColor.BLACK
            } else {
                val pinAtPos = board.getPinAt(pos)
                val pinColor = pinAtPos?.getEffectiveColor()
                pinAtPos != null && pinColor == color && pinColor != PinColor.BLACK
            }
        }
        
        if (validCrossPositions.size == 5) {
            val isOnGridLines = isCrossOnGridLines(validCrossPositions)
            val points = if (isOnGridLines) 10 else 5
            return PatternResult(points, validCrossPositions)
        }
        
        return PatternResult(0, emptyList())
    }
    
    private fun findAllCrossPatterns(board: Board, position: Position, color: PinColor): PatternResult {
        val (row, col) = position.toLegacy()
        
        var bestCrossResult = PatternResult(0, emptyList())
        
        val possibleCenters = listOfNotNull(
            position,
            if (row + 1 <= 9) Position.fromLegacy(row + 1, col) else null,
            if (row - 1 >= 0) Position.fromLegacy(row - 1, col) else null,
            if (col + 1 <= 9) Position.fromLegacy(row, col + 1) else null,
            if (col - 1 >= 0) Position.fromLegacy(row, col - 1) else null
        )
        
        for (centerPos in possibleCenters) {
            val crossResult = findCrossPattern(board, position, centerPos, color)
            if (crossResult.points > bestCrossResult.points) {
                bestCrossResult = crossResult
            }
        }
        
        return bestCrossResult
    }
    
    private fun getScoringPositions(placedPosition: Position, patternsFormed: List<List<Position>>): List<Position> {
        val allPositions = mutableSetOf<Position>()
        
        // Always include the placed pin position
        allPositions.add(placedPosition)
        
        // Add all positions from formed patterns
        patternsFormed.forEach { pattern ->
            allPositions.addAll(pattern)
        }
        
        return allPositions.toList()
    }
    
    private fun isSquareOffGridLines(positions: List<Position>): Boolean {
        val gridLines = setOf(0, 3, 6, 9)
        return positions.none { pos ->
            val (row, col) = pos.toLegacy()
            row in gridLines || col in gridLines
        }
    }
    
    private fun isCrossOnGridLines(positions: List<Position>): Boolean {
        val gridLines = setOf(0, 3, 6, 9)
        return positions.all { pos ->
            val (row, col) = pos.toLegacy()
            row in gridLines || col in gridLines
        }
    }
}

data class ScoreResult(
    val points: Int,
    val pinsToReverse: List<Position>,
    val linesFormed: List<List<Position>>,
    val scoringPositions: List<Position>  // Always includes the placed pin position
)

data class PatternResult(
    val points: Int,
    val positions: List<Position>
)
